import { ACCEPT_JSON, ApiError } from '@graal/api';
import { runtimeEnv } from '@/lib/env';

/**
 * Lookup public `tenant → realm`.
 *
 * `GET /tenants/{tenantId}?attribute=realm` est la **seule** opération du contrat qui ne porte pas
 * `X-Tenant` : elle est appelée avant toute authentification, pour savoir quel realm Keycloak
 * interroger (un realm par tenant, cf. dev/keycloak/README.md §1). Le realm n'est donc jamais une
 * variable de build.
 *
 * Le chemin porte déjà une query string dans la clé : on la préserve (même défaut que les 9 chemins
 * traités par `normalizeUrl` dans @graal/api).
 */
export type TenantRealm = { tenantId: string; realm: string; name: string };

const cache = new Map<string, TenantRealm>();

export function cachedRealm(tenantId: string): TenantRealm | undefined {
  return cache.get(tenantId);
}

export function rememberRealm(entry: TenantRealm): void {
  cache.set(entry.tenantId, entry);
}

export function clearRealmCache(): void {
  cache.clear();
}

export async function lookupRealm(tenantId: string, signal?: AbortSignal): Promise<TenantRealm> {
  const known = cache.get(tenantId);
  if (known) return known;

  const base = runtimeEnv().apiUrl.replace(/\/+$/, '');
  const response = await fetch(`${base}/tenants/${encodeURIComponent(tenantId)}?attribute=realm`, {
    // `application/json` n'est pas jugé compatible avec les médias types vnd du backend : voir
    // `ACCEPT_JSON` dans @graal/api (mesuré : 404 sur les routes à prédicat `accept`).
    headers: { Accept: ACCEPT_JSON },
    credentials: 'omit',
    ...(signal ? { signal } : {}),
  });

  const text = response.status === 204 ? '' : await response.text();
  const payload: unknown = text ? (JSON.parse(text) as unknown) : undefined;
  if (!response.ok) throw new ApiError(response.status, payload);

  const realm = (payload as { realm?: unknown } | undefined)?.realm;
  if (typeof realm !== 'string' || realm.length === 0) {
    // Le contrat ne rend `realm` obligatoire nulle part : un 200 sans realm est un tenant inutilisable.
    throw new ApiError(response.status, payload, 'realm absent de la réponse du lookup');
  }
  const name = (payload as { name?: unknown }).name;
  const entry: TenantRealm = {
    tenantId,
    realm,
    name: typeof name === 'string' && name.length > 0 ? name : tenantId,
  };
  cache.set(tenantId, entry);
  return entry;
}

/**
 * F9.11 (2026-10-11) : la console servie sur le domaine personnalisé d'un locataire en déduit le
 * locataire — `GET /domains/{host}` (public, comme le lookup ci-dessus). `undefined` : aucun locataire
 * ne déclare cet hôte (404), ou l'hôte n'est pas un nom de domaine (boucle locale, adresse IP) — la
 * console garde alors son parcours ordinaire (dernier locataire, sinon /login).
 */
const domainCache = new Map<string, TenantRealm | null>();

export function clearDomainCache(): void {
  domainCache.clear();
}

const isDomainName = (host: string) =>
  host.includes('.') && !/^[0-9.]+$/u.test(host) && !host.includes(':') && host !== 'localhost';

export async function lookupDomain(host: string, signal?: AbortSignal): Promise<TenantRealm | undefined> {
  const name = host.toLowerCase();
  if (!isDomainName(name)) return undefined;
  if (domainCache.has(name)) return domainCache.get(name) ?? undefined;
  const base = runtimeEnv().apiUrl.replace(/\/+$/, '');
  let response: Response;
  try {
    response = await fetch(`${base}/domains/${encodeURIComponent(name)}`, {
      headers: { Accept: 'application/json' },
      credentials: 'omit',
      ...(signal ? { signal } : {}),
    });
  } catch {
    return undefined;
  }
  if (!response.ok) {
    if (response.status === 404) domainCache.set(name, null);
    return undefined;
  }
  const payload = (await response.json()) as { id?: unknown; realm?: unknown };
  if (typeof payload.id !== 'string' || typeof payload.realm !== 'string') return undefined;
  const entry: TenantRealm = { tenantId: payload.id, realm: payload.realm, name: payload.id };
  domainCache.set(name, entry);
  rememberRealm(entry);
  return entry;
}
